import React from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { CentralAmparoPageHeader } from '../../components/centralAmparo/CentralAmparoPageHeader'
import { modulesService } from '../../services/modules'
import CalendarioAgendaTab from './CalendarioAgendaTab'
import CalendarioFormulariosPendentesTab from './CalendarioFormulariosPendentesTab'

const MODULE_NAME = 'central_amparo_calendario'
const DEFAULT_MODULE_TITLE = 'Calendário'

type HeaderAction = {
  visible: boolean
  disabled: boolean
  onClick: () => void
}

type CalendarioPageTab = 'agenda' | 'formularios_pendentes'

const CentralAmparoCalendario: React.FC = () => {
  const [moduleInfo, setModuleInfo] = React.useState<{
    display_name: string
    description: string | null
  } | null>(null)
  const [activeTab, setActiveTab] = React.useState<CalendarioPageTab>('agenda')
  const [pendingFormsCount, setPendingFormsCount] = React.useState<number | null>(null)
  const createClickRef = React.useRef<() => void>(() => {})
  const unavailClickRef = React.useRef<() => void>(() => {})
  const [showCreateAppointment, setShowCreateAppointment] = React.useState(false)
  const [createAppointmentDisabled, setCreateAppointmentDisabled] = React.useState(false)
  const [showUnavailability, setShowUnavailability] = React.useState(false)
  const [unavailabilityDisabled, setUnavailabilityDisabled] = React.useState(false)

  const handlePendingCountChange = React.useCallback((count: number | null) => {
    setPendingFormsCount(count)
  }, [])

  const handleCreateActionChange = React.useCallback((action: HeaderAction | null) => {
    if (!action?.visible) {
      setShowCreateAppointment(false)
      return
    }
    createClickRef.current = action.onClick
    setShowCreateAppointment(true)
    setCreateAppointmentDisabled(action.disabled)
  }, [])

  const handleUnavailabilityActionChange = React.useCallback((action: HeaderAction | null) => {
    if (!action?.visible) {
      setShowUnavailability(false)
      return
    }
    unavailClickRef.current = action.onClick
    setShowUnavailability(true)
    setUnavailabilityDisabled(action.disabled)
  }, [])

  const tabClass = (tab: CalendarioPageTab) =>
    `whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
      activeTab === tab
        ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-300'
        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
    }`

  React.useEffect(() => {
    const loadModule = async () => {
      const result = await modulesService.getByName(MODULE_NAME)
      if (result.success && result.data) {
        setModuleInfo({
          display_name: result.data.display_name || DEFAULT_MODULE_TITLE,
          description: result.data.description || null,
        })
      }
    }
    void loadModule()
  }, [])

  React.useEffect(() => {
    if (activeTab !== 'agenda') {
      setShowCreateAppointment(false)
      setShowUnavailability(false)
    }
  }, [activeTab])

  const headerActions =
    activeTab === 'agenda' && (showUnavailability || showCreateAppointment) ? (
      <>
        {showUnavailability ? (
          <Button
            variant="outline"
            onClick={() => unavailClickRef.current()}
            disabled={unavailabilityDisabled}
          >
            Indisponibilidade
          </Button>
        ) : null}
        {showCreateAppointment ? (
          <Button
            onClick={() => createClickRef.current()}
            disabled={createAppointmentDisabled}
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            Novo agendamento
          </Button>
        ) : null}
      </>
    ) : null

  return (
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        fallback={
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar esta página.
              </Text>
            </div>
          </div>
        }
      >
        <div className="h-full flex flex-col">
          <div className="px-6 pt-6">
            <div className="mb-6">
              <CentralAmparoPageHeader
                title={moduleInfo?.display_name ?? DEFAULT_MODULE_TITLE}
                description={
                  moduleInfo?.description || 'Agenda da Central de Amparo.'
                }
                actions={headerActions}
              />
            </div>

            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Abas do calendário">
                <button
                  type="button"
                  onClick={() => setActiveTab('agenda')}
                  className={tabClass('agenda')}
                >
                  Agenda
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('formularios_pendentes')}
                  className={tabClass('formularios_pendentes')}
                >
                  Formulários pendentes
                  {pendingFormsCount !== null ? ` (${pendingFormsCount})` : ''}
                </button>
              </nav>
            </div>
          </div>

          <div className="flex-1 p-6 space-y-4">
            {activeTab === 'agenda' ? (
              <CalendarioAgendaTab
                onCreateActionChange={handleCreateActionChange}
                onUnavailabilityActionChange={handleUnavailabilityActionChange}
              />
            ) : null}
            <CalendarioFormulariosPendentesTab
              active={activeTab === 'formularios_pendentes'}
              onPendingCountChange={handlePendingCountChange}
            />
          </div>
        </div>
      </PermissionGuard>
    </main>
  )
}

export default CentralAmparoCalendario
